<script setup lang="ts">
import {computed, watch} from "vue";
import {CircleCheckBig, ScanLine} from "@lucide/vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import BaseAlert from "@/core/components/base/BaseAlert.vue";
import useBarcodeScanner from "@/core/composables/barcode/useBarcodeScanner";
import useGiftScan from "@/birthday-gift/composables/useGiftScan";
import GiftScanFoundPanel from "@/birthday-gift/components/scan/GiftScanFoundPanel.vue";
import GiftScanClosedPanel from "@/birthday-gift/components/scan/GiftScanClosedPanel.vue";

const props = defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'redeemed'): void;
}>();

const {state, result, manualCode, busy, error, check, redeem, reset} = useGiftScan();

// The reader types its burst wherever the focus is: listen while the dialog
// is open, and let the manual field keep working for slow typing.
useBarcodeScanner({
    enabled: () => props.open,
    onScan: (code) => {
        manualCode.value = code;
        check(code);
    },
});

watch(() => props.open, (open) => {
    if (open) {
        reset();
    }
});

const showIdle = computed(() => ['idle', 'checking', 'unknown'].includes(state.value));

const confirmRedeem = async () => {
    await redeem();

    if (state.value === 'done') {
        emit('redeemed');
    }
};

const close = () => emit('update:open', false);
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="max-w-xl">
      <DialogHeader>
        <DialogTitle>Retrait d'un cadeau au comptoir</DialogTitle>
      </DialogHeader>

      <template v-if="showIdle">
        <div class="flex flex-col items-center gap-2.5 rounded-xl border-[1.5px] border-dashed border-primary bg-primary-lightest px-5 py-6 text-center">
          <ScanLine class="h-10 w-10 text-primary" />
          <div class="text-sm font-semibold">
            Scannez le QR code du patient avec votre lecteur Datamatrix
          </div>
          <div class="max-w-[400px] text-xs leading-relaxed text-gray-dark">
            Celui que vous utilisez pour les boîtes de médicaments. Visez le QR code affiché sur
            son téléphone — la lecture remplit le champ ci-dessous toute seule.
          </div>
        </div>

        <div>
          <Label>Ou saisissez le code manuellement</Label>
          <div class="mt-1.5 flex flex-wrap gap-2.5">
            <Input
              v-model="manualCode"
              placeholder="4K7Q-2M3N"
              class="flex-1 font-mono uppercase tracking-widest"
              @keyup.enter="check(manualCode)"
            />
            <Button
              :disabled="busy || !manualCode.trim()"
              @click="check(manualCode)"
            >
              Vérifier
            </Button>
          </div>
        </div>

        <BaseAlert
          v-if="state === 'unknown'"
          type="error"
          title="Code non reconnu"
        >
          Ce code ne correspond à aucun cadeau d'anniversaire. Vérifiez le code affiché sur le
          téléphone du patient et rescannez.
        </BaseAlert>
      </template>

      <GiftScanFoundPanel
        v-else-if="state === 'found' || state === 'redeeming'"
        :result="result"
        :busy="busy"
        :error="error"
        @cancel="reset"
        @redeem="confirmRedeem"
      />

      <GiftScanClosedPanel
        v-else-if="state === 'already' || state === 'expired'"
        :result="result"
        :expired="state === 'expired'"
        @reset="reset"
      />

      <div
        v-else-if="state === 'done'"
        class="flex flex-col items-center gap-3 py-3 text-center"
      >
        <span class="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
          <CircleCheckBig class="h-8 w-8 text-primary-dark" />
        </span>
        <h3 class="text-lg font-bold text-primary">
          Cadeau remis à {{ result?.patient?.first_name }} 🥳
        </h3>
        <p class="max-w-[420px] text-sm leading-relaxed text-gray-dark">
          Le code {{ result?.code }} est désormais invalide. Le retrait apparaît dans les
          statistiques du cadeau.
        </p>
        <div class="mt-1 flex flex-wrap justify-center gap-2.5">
          <Button
            variant="ghost"
            @click="reset"
          >
            Scanner un autre code
          </Button>
          <Button @click="close">
            Terminer
          </Button>
        </div>
      </div>

      <div
        v-if="state === 'checking'"
        class="text-center text-xs text-muted-foreground"
      >
        Vérification du code…
      </div>
    </DialogContent>
  </Dialog>
</template>
